/**
 * Admin Dashboard Home Page
 * 
 * Overview page with key stats and recent activity.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Megaphone, 
  DollarSign, 
  TrendingUp,
  ArrowRight,
  Eye,
  UserPlus,
  Activity,
} from 'lucide-react';
import { AdminLayout, StatsCard } from '../../components/admin';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface InfluencerSummary {
  id: string;
  name: string;
  slug: string;
  totalClicks: number;
  totalSignups: number;
}

const AdminDashboard: React.FC = () => {
  const [influencers, setInfluencers] = useState<InfluencerSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/influencer/admin/list?includeInactive=true`);
      const result = await response.json();
      if (result.success) {
        setInfluencers(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalClicks = influencers.reduce((sum, inf) => sum + inf.totalClicks, 0);
  const totalSignups = influencers.reduce((sum, inf) => sum + inf.totalSignups, 0);
  const conversionRate = totalClicks > 0 ? ((totalSignups / totalClicks) * 100).toFixed(1) : '0';

  return (
    <AdminLayout 
      title="Dashboard" 
      subtitle="Overview of your TradeRewind admin panel"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatsCard
          title="Total Influencers"
          value={influencers.length}
          icon={Megaphone}
          color="purple"
          trend={{ value: 12, label: 'vs last month' }}
        />
        <StatsCard
          title="Total Clicks"
          value={totalClicks.toLocaleString()}
          icon={Eye}
          color="blue"
          trend={{ value: 8, label: 'vs last month' }}
        />
        <StatsCard
          title="Total Signups"
          value={totalSignups.toLocaleString()}
          icon={UserPlus}
          color="green"
          trend={{ value: 15, label: 'vs last month' }}
        />
        <StatsCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          icon={TrendingUp}
          color="orange"
          trend={{ value: 2.3, label: 'vs last month' }}
        />
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/admin/influencers"
                className="flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Manage Influencers</p>
                    <p className="text-xs text-slate-500">Add, edit, or remove</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
              </Link>

              <Link
                to="/admin/users"
                className="flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">View Users</p>
                    <p className="text-xs text-slate-500">Manage user accounts</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
              </Link>

              <Link
                to="/admin/billing"
                className="flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Billing Overview</p>
                    <p className="text-xs text-slate-500">Revenue & subscriptions</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
              </Link>
            </div>
          </div>
        </div>

        {/* Top Influencers */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Top Influencers</h3>
              <Link 
                to="/admin/influencers" 
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                View all
              </Link>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : influencers.length === 0 ? (
              <div className="text-center py-8">
                <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No influencers yet</p>
                <Link 
                  to="/admin/influencers" 
                  className="text-sm text-blue-400 hover:text-blue-300 mt-2 inline-block"
                >
                  Add your first influencer
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="pb-3">Influencer</th>
                      <th className="pb-3 text-right">Clicks</th>
                      <th className="pb-3 text-right">Signups</th>
                      <th className="pb-3 text-right">Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {influencers.slice(0, 5).map((influencer) => {
                      const rate = influencer.totalClicks > 0 
                        ? ((influencer.totalSignups / influencer.totalClicks) * 100).toFixed(1)
                        : '0';
                      return (
                        <tr key={influencer.id} className="hover:bg-slate-800/30">
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {influencer.name.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">{influencer.name}</p>
                                <p className="text-xs text-slate-500">/{influencer.slug}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 text-right text-sm text-slate-300">
                            {influencer.totalClicks.toLocaleString()}
                          </td>
                          <td className="py-3 text-right text-sm text-slate-300">
                            {influencer.totalSignups.toLocaleString()}
                          </td>
                          <td className="py-3 text-right">
                            <span className={`text-sm font-medium ${
                              parseFloat(rate) >= 5 ? 'text-green-400' :
                              parseFloat(rate) >= 2 ? 'text-yellow-400' : 'text-slate-400'
                            }`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {[
              { icon: UserPlus, text: 'New user signed up via tradingguru', time: '2 minutes ago', color: 'text-green-400' },
              { icon: Eye, text: 'cryptoqueen landing page viewed', time: '5 minutes ago', color: 'text-blue-400' },
              { icon: Activity, text: 'daytraderdan reached 100 clicks', time: '1 hour ago', color: 'text-purple-400' },
              { icon: UserPlus, text: 'New user signed up via cryptoqueen', time: '2 hours ago', color: 'text-green-400' },
            ].map((activity, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 bg-slate-800/30 rounded-lg">
                <div className={`w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center ${activity.color}`}>
                  <activity.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-300">{activity.text}</p>
                  <p className="text-xs text-slate-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
